import { Subject, BehaviorSubject, Observable, of } from 'rxjs';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';

import { Injectable, OnDestroy, Inject, Optional } from '@angular/core';
import { Title } from '@angular/platform-browser';

import { SnComponentUpdater } from '@lib/core';
import { SnRouterHelperService, mapToRouteDataItem } from '@lib/state/router';
import { TranslationService, TranslationKey } from '@lib/translation';
import { SN_BRAND_NAME } from '@lib/ui/brand';

import { SnTitleUpdateEvent } from '../types/title-update-event.type';

@Injectable()
export class SnTitleService implements OnDestroy {
  private _title: string;
  private readonly _title$ = new BehaviorSubject<string>('');

  private readonly _destroy$ = new Subject<void>();

  constructor(
    @Optional() @Inject(SN_BRAND_NAME) private readonly _brandName: string,
    private readonly _routerHelper: SnRouterHelperService,
    private readonly _componentUpdater: SnComponentUpdater<SnTitleUpdateEvent>,
    private readonly _ngTitle: Title,
    @Optional() private readonly _translationService?: TranslationService,
  ) {
    this._ngTitle.setTitle(this._brandName);
    this._routeChangeSubscribe();
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public setTitle(title: string): void {
    const title$ = this._translationService ? this._translationService.setTitle(title as TranslationKey) : of(title);
    title$.pipe(takeUntil(this._destroy$)).subscribe((translatedTitle) => {
      this._title = translatedTitle;
      this._title$.next(this._title);
      this._ngTitle.setTitle(`${this._brandName} - ${this._title}`);
      this._componentUpdater.updateComponent('sn-title', title);
    });
  }

  public getTitle(): string {
    return this._title;
  }

  public getTitle$(): Observable<string> {
    return this._title$.asObservable();
  }

  private _routeChangeSubscribe(): void {
    this._routerHelper.routeChange$
      .pipe(mapToRouteDataItem('snTitle', true), distinctUntilChanged(), takeUntil(this._destroy$))
      .subscribe((title) => {
        this.setTitle(title);
      });
  }
}
